//askSmart-ui/src/pages/ManageSMRTDocs.tsx
import React, { useEffect, useRef, useState } from "react";
import "./ManageProfiles.css";
import "./Overview.css";
import ManageSMRTDocsWizard from "./ManageSMRTDocsWizard";
import SMRTDocsEditor from "./SMRTDocsEditor";
import { useNavigate } from "react-router-dom";
import SMRTDocsTrafficModal, { getRedItems, getYellowItems, getGreenItems, getServiceLocalDocs } from "../components/SMRTDocsTrafficModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useAuth } from "../hooks/useAuth";
import { findMaster, isSubService, ownContent } from "./smrtdocsServices";

/**
 * A service's permanent name in the data — unlike its position in the array,
 * which changes whenever an earlier service is deleted, and unlike its display
 * name, which an admin can rename at will.
 */
export const newServiceId = (): string =>
  `svc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// One encrypted-field reveal, as surfaced by GET /smrtdocs/sensitive-access-log.
// Kept separate from the Profiles SensitiveAccessEvent shape on purpose — the
// two modules' audit trails are independent.
interface SmrtDocsAccessEvent {
  timestamp: string;
  ownerUserId: string;
  issuedKey: string;
  placementId: string;
  revealedByUserID: string;
  reason: string;
  ipAddress: string;
  userAgent: string;
}

export default function ManageSMRTDocs() {
  const [activeTab, setActiveTab] = useState<
    "overview" | "new" | "auditLog"
  >("overview");

  // Encrypted-field reveal audit log — visible to smrtdocs.admin OR
  // smrtdocs.sensitive. Fetched lazily when the tab is first opened so the
  // common case (tab never clicked) pays zero network cost.
  const { roles } = useAuth();
  const canViewAuditLog =
    Array.isArray(roles?.permissions) &&
    (roles.permissions.includes("smrtdocs.admin") ||
      roles.permissions.includes("smrtdocs.sensitive"));
  const [auditLogEvents, setAuditLogEvents] = useState<SmrtDocsAccessEvent[]>(
    [],
  );
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogError, setAuditLogError] = useState<string | null>(null);
  const auditLogLoadedRef = useRef(false);
  const auditTh: React.CSSProperties = {
    padding: "8px 10px",
    borderBottom: "1px solid #2a2a2a",
  };
  const [isTrafficModalOpen, setIsTrafficModalOpen] = useState(false);
  const [trafficFilter, setTrafficFilter] = useState<"red" | "yellow" | "green" | null>(null);
  // `blocked` marks a message that only explains why something can't be done.
  // It has no destructive action behind it, so the red "Delete" it would
  // otherwise offer is replaced by a plain acknowledgement.
  const [deleteConfirm, setDeleteConfirm] = useState<{ title: string; message: string; onConfirm: () => void; blocked?: boolean } | null>(null);
  const [renameModal, setRenameModal] = useState<{ index: number; value: string } | null>(null);
  /** The + chooser: start a service from nothing, or from a copy. */
  const [showNewServicePanel, setShowNewServicePanel] = useState(false);
  /**
   * The name prompt, opened by "New Service" and by "Master" — null when it is
   * closed. Kind travels with the name rather than in a flag beside it, so the
   * two can't be left disagreeing by a path that resets one and not the other.
   */
  const [newServiceDraft, setNewServiceDraft] = useState<
    { name: string; isMaster: boolean } | null
  >(null);
  /**
   * Which service "Duplicate Existing" is copying, and whether the copy is
   * being made as a Master. Both routes into a copy share this modal.
   */
  const [duplicateModal, setDuplicateModal] = useState<
    { sourceIndex: number; isMaster: boolean } | null
  >(null);
  /** The Master sub-chooser: build one from nothing, or from a copy. */
  const [showMasterPanel, setShowMasterPanel] = useState(false);
  /**
   * A Sub Service being named. `name` is its own half only — the Master's half
   * is prepended on create — and parentIndex is the Master it will hang from.
   */
  const [subServiceDraft, setSubServiceDraft] = useState<
    { name: string; parentIndex: number } | null
  >(null);
  /** Shown when "Sub Service" is picked with no Master to attach it to. */
  const [noMasterWarning, setNoMasterWarning] = useState(false);
  /** Whether the added-service tab row is showing. Closed until asked for. */
  const [serviceTabsOpen, setServiceTabsOpen] = useState(false);
  /**
   * Which Masters have their Sub Services showing, by Master id — an org with
   * a service per tenant would otherwise fill the strip. Closed until asked
   * for, except the one that just gained a child.
   */
  const [masterOpen, setMasterOpen] = useState<Record<string, boolean>>({});
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<
    Record<
      string,
      { mode: "single"; value: number } | { mode: "multi"; values: number[] }
    >
  >({});
  const [multiOpen, setMultiOpen] = useState<Record<string, boolean>>({});

  // Overview sort, persisted so the admin's last choice is restored next visit.
  type SortCol = "name" | "email" | "services" | "lastLogin";
  const [sort, setSort] = useState<{ col: SortCol; dir: "asc" | "desc" }>(() => {
    try {
      const raw = localStorage.getItem("smrtdocs-overview-sort");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.col && (parsed.dir === "asc" || parsed.dir === "desc")) {
          return parsed;
        }
      }
    } catch {
      /* ignore malformed storage */
    }
    return { col: "name", dir: "asc" };
  });
  useEffect(() => {
    try {
      localStorage.setItem("smrtdocs-overview-sort", JSON.stringify(sort));
    } catch {
      /* ignore */
    }
  }, [sort]);
  const toggleSort = (col: SortCol) =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" },
    );
  const sortArrow = (col: SortCol) =>
    sort.col !== col ? (
      <span style={{ opacity: 0.3, marginLeft: 4, fontSize: "0.7em" }}>▼</span>
    ) : (
      <span style={{ marginLeft: 4, fontSize: "0.7em" }}>
        {sort.dir === "asc" ? "▲" : "▼"}
      </span>
    );

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/assignments`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        const parsed = data.assignments || {};

        // normalize old single values into arrays
        const normalized: Record<
          string,
          { mode: "single"; value: number } | { mode: "multi"; values: number[] }
        > = {};

        Object.keys(parsed).forEach((key) => {
          const val = parsed[key];

          if (val?.mode === "multi") {
            const values: number[] = (val.values || []).filter(
              (v: any) => typeof v === "number",
            );

            // ONE service is a single assignment, whichever panel recorded it.
            //
            // The ☑ panel writes `multi` the moment it opens, so a user given one
            // service through it is stored as multi with a one-element array. The
            // row then read "Multiple" after a refresh while naming the service
            // correctly before one — the same assignment described two ways
            // depending on whether it had been round the server yet.
            //
            // Collapsed on read rather than migrated: the stored shape is
            // harmless and every reader goes through here, so there is nothing to
            // rewrite in the database.
            if (values.length === 1) {
              normalized[key] = { mode: "single", value: values[0] };
            } else if (values.length > 1) {
              normalized[key] = { mode: "multi", values };
            }
            // An empty multi is nobody assigned to anything: the key is dropped
            // rather than kept, so the row reads "- Please Select -" instead of
            // claiming several services and listing none.
          } else if (val?.mode === "single") {
            normalized[key] = {
              mode: "single",
              value: val.value,
            };
          }
        });

        setAssignments(normalized);
        assignmentsLoaded.current = true;
      })
      .catch(err => console.error("Failed to load assignments from backend:", err));
  }, []);

  const assignmentsLoaded = useRef(false);

  useEffect(() => {
    if (!assignmentsLoaded.current) return;

    // `_prev` is the multi panel's undo buffer — what the row held before the
    // panel was opened, so Cancel can put it back. It is UI state and was being
    // persisted with everything else, which left a copy of a superseded
    // assignment inside the live one on the server for anybody reading the
    // container to puzzle over.
    const forServer = Object.fromEntries(
      Object.entries(assignments).map(([userId, value]) => {
        const { _prev, ...rest } = value as any;
        return [userId, rest];
      }),
    );

    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/assignments`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assignments: forServer }),
    }).catch(err => console.error("Failed to save assignments:", err));
  }, [assignments]);

  const [services, setServices] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const servicesLoaded = useRef(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/services`, { credentials: "include" })
      .then(async r => {
        // A 403/500 still resolves the fetch. Only enable auto-save once the
        // load genuinely succeeds — otherwise an empty initial state could be
        // PUT back over real data, and saves would appear to "not persist".
        if (!r.ok) {
          setServicesError(
            r.status === 403
              ? "You don't have permission to load SMRT Docs services. If your role was changed recently, sign out and back in."
              : `Failed to load services (${r.status}).`
          );
          return;
        }
        const data = await r.json();
        // Ids are backfilled server-side on read, so every client — admin
        // page, tenant portal, admin's view of a user — sees the same ones.
        setServices(data.services || []);
        servicesLoaded.current = true;
        setServicesError(null);
      })
      .catch(err => {
        console.error("Failed to load services from backend:", err);
        setServicesError("Failed to load SMRT Docs services. Check your connection and reload.");
      });
  }, []);

  useEffect(() => {
    if (!servicesLoaded.current) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/services`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ services }),
    })
      .then(r => {
        if (!r.ok) {
          setServicesError(
            r.status === 403
              ? "Changes were not saved — you don't have permission. If your role was changed recently, sign out and back in."
              : `Changes were not saved (${r.status}).`
          );
        } else {
          setServicesError(null);
        }
      })
      .catch(err => {
        console.error("Failed to save services:", err);
        setServicesError("Changes were not saved — check your connection.");
      });
  }, [services]);

  // Lazy-load the audit log the first time the Audit Log tab is opened.
  useEffect(() => {
    if (activeTab !== "auditLog") return;
    if (auditLogLoadedRef.current) return;
    if (!canViewAuditLog) return;
    auditLogLoadedRef.current = true;
    setAuditLogLoading(true);
    setAuditLogError(null);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/sensitive-access-log`,
      { credentials: "include" },
    )
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `Failed (${res.status})`);
        }
        return res.json();
      })
      .then((data: SmrtDocsAccessEvent[]) => {
        setAuditLogEvents(Array.isArray(data) ? data : []);
      })
      .catch((err: any) => {
        setAuditLogError(err?.message || "Failed to load audit log");
        auditLogLoadedRef.current = false; // allow retry on tab re-open
      })
      .finally(() => setAuditLogLoading(false));
  }, [activeTab, canViewAuditLog]);
  const [activeServiceIndex, setActiveServiceIndex] = useState<number | null>(
    null,
  );

  const [userStates, setUserStates] = useState<Record<string, any>>({});
  const [overrides, setOverrides] = useState<Record<string, any>>({});

  const navigate = useNavigate();
  const loadUsers = () => {
    setUsersLoading(true);
    setUsersError(null);
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, { credentials: "include" }).then(r => {
        if (!r.ok) throw new Error(`approved_users responded ${r.status}`);
        return r.json();
      }),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/roles?scope=all`, { credentials: "include" }).then(r => r.json()),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/overrides`, { credentials: "include" }).then(r => r.json()),
    ])
      .then(([data, rolesData, ovData]) => {
        const allUsers = Array.isArray(data) ? data : [];
        const orgRoles = rolesData?.org || [];
        const docsRoleIDs = new Set(
          orgRoles
            .filter((r: any) => r.permissions?.includes('smrtdocs.view') || r.permissions?.includes('smrtdocs.admin'))
            .map((r: any) => r.id)
        );
        // Collaborators carry smrtdocs.view (so they'd pass the docs filter),
        // but they are managed from the User module and a collaborator is only
        // ever attached to another user's view — they are not assignable
        // services here and shouldn't clutter the admin to-do list. Drop any
        // user holding the Collaborator system role from this overview.
        const collaboratorRoleIDs = new Set(
          orgRoles
            .filter((r: any) => r.name === 'Collaborator')
            .map((r: any) => r.id)
        );
        const usersList = allUsers.filter((u: any) => {
          const rids = u.customRoles || [];
          if (rids.some((rid: string) => collaboratorRoleIDs.has(rid))) return false;
          return rids.some((rid: string) => docsRoleIDs.has(rid));
        });
        setUsers(usersList);
        setOverrides(ovData.overrides || {});

        // Fetch user states for all filtered users
        const userIds = usersList.map((u: any) => u.user_id || u.id).filter(Boolean);
        Promise.all(
          userIds.map((uid: string) =>
            fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(uid)}`, { credentials: "include" })
              .then(r => r.json())
              .then(state => ({ uid, state }))
              .catch(() => ({ uid, state: {} }))
          )
        ).then(results => {
          const states: Record<string, any> = {};
          for (const { uid, state } of results) states[uid] = state;
          setUserStates(states);
        });
      })
      .catch((err) => {
        console.error("Failed to load users", err);
        setUsersError("Couldn't load the user list. Please try again.");
      })
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Human-readable assignment label, reused for sorting the Assigned column.
  const assignmentLabel = (uid: string) => {
    const a = assignments[uid];
    if (!a) return "";
    if (a.mode === "multi") return "Multiple";
    if (a.mode === "single") return services[a.value]?.name || "";
    return "";
  };
  const sortValue = (u: any, col: SortCol): string | number => {
    switch (col) {
      case "name":
        return (u.name || u.email || "").toLowerCase();
      case "email":
        return (u.email || "").toLowerCase();
      case "services":
        return assignmentLabel(u.user_id).toLowerCase();
      case "lastLogin":
        return u.last_login ? new Date(u.last_login).getTime() : 0;
      default:
        return "";
    }
  };
  const sortedUsers = [...users].sort((a, b) => {
    const va = sortValue(a, sort.col);
    const vb = sortValue(b, sort.col);
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb));
    return sort.dir === "asc" ? cmp : -cmp;
  });

  // A Sub Service inherits its documents from a Master, so one has to exist
  // before there is anything to attach it to.
  const hasMaster = services.some((svc: any) => svc?.isMaster);

  /**
   * A Sub Service's stored name is its Master's name plus its own.
   *
   * Stored in full, rather than assembled at every render, so that every
   * existing reader of `svc.name` — the assignment dropdown, the certificate
   * label resolver, TOSP's tenant-service match — keeps working untouched.
   * The price is that renaming a Master has to rewrite its children, which
   * commitRename below does.
   */
  const subServiceName = (masterName: string, subName: string): string =>
    `${masterName} - ${subName}`;

  // How the tab strip is grouped: standalone services on one row, then each
  // Master on its own row with its Sub Services beneath it. All three lists
  // hold INDICES into `services`, which is what serviceTab and everything
  // downstream of it addresses a service by.
  const allIndices = services.map((_: any, i: number) => i);
  const masterIndices = allIndices.filter((i: number) => services[i]?.isMaster);
  const childIndices = (masterIndex: number): number[] => {
    const masterId = services[masterIndex]?.id;
    if (!masterId) return [];
    return allIndices.filter((i: number) => services[i]?.parentId === masterId);
  };
  // A child whose parentId names no Master would otherwise be grouped under
  // nothing and disappear from the strip entirely, so it falls back to the
  // standalone row: visible and editable rather than silently gone.
  const groupedIndices = new Set<number>([
    ...masterIndices,
    ...masterIndices.flatMap((mi: number) => childIndices(mi)),
  ]);
  const standaloneIndices = allIndices.filter(
    (i: number) => !groupedIndices.has(i),
  );
  /**
   * What the strip's own toggle counts: everything that gets a row of its own.
   *
   * Sub Services are excluded — each is counted by the Master it hangs from,
   * and counting them in both places would say an org has more services than
   * it has tabs to show.
   */
  const topLevelCount =
    standaloneIndices.length + masterIndices.length;

  // Which services a copy may be made from, as INDICES into `services` — the
  // value the rest of the duplicate flow speaks.
  //
  // A Master may be built from anything, another Master included. A plain
  // service may not be built from a Master: the Master is an abstract
  // template, so a concrete copy of one would inherit nothing and sit there
  // looking like a Sub Service that had quietly failed.
  const masterCopySources = allIndices;
  const serviceCopySources = masterCopySources.filter(
    (i: number) => !services[i]?.isMaster,
  );
  /** The source a copy opens on: the tab in view when it qualifies. */
  const defaultCopySource = (sources: number[]): number =>
    activeServiceIndex !== null && sources.includes(activeServiceIndex)
      ? activeServiceIndex
      : sources[0];

  // Both routes into a new service end the same way: appended to the list,
  // selected, and the row it lives on opened so the editor isn't sitting on a
  // tab that's off screen. Appending — never inserting — matters: assignments
  // are stored as indices into this array, so an earlier position would
  // repoint every user's assignment.
  const addService = (service: {
    name: string;
    data: any;
    isMaster?: boolean;
    /** A Sub Service's own half of its name, kept for renames and display. */
    subName?: string;
    /** The Master's permanent id — never its position. */
    parentId?: string;
  }) => {
    const updated = [...services, { id: newServiceId(), ...service }];
    setServices(updated);
    setActiveServiceIndex(updated.length - 1);
    setActiveTab("new");
    setIsCreating(true);
    setServiceTabsOpen(true);
    setShowNewServicePanel(false);
  };

  // A Master is an ordinary empty service in every respect except that it
  // carries isMaster, which keeps it out of the places a user could be put on
  // it. The flag is only set here, never inferred from the name.
  const createBlankService = () => {
    const name = (newServiceDraft?.name || "").trim();
    if (!name) return;
    addService({
      name,
      data: { steps: [] },
      ...(newServiceDraft?.isMaster ? { isMaster: true } : {}),
    });
    setNewServiceDraft(null);
  };

  // A Sub Service starts empty: its documents come from its Master, so copying
  // the Master's steps here would be a snapshot that stops tracking it. They
  // are merged in on every render by resolveServiceSteps instead.
  const createSubService = () => {
    if (!subServiceDraft) return;
    const subName = subServiceDraft.name.trim();
    const master = services[subServiceDraft.parentIndex];
    if (!subName || !master?.id) return;
    addService({
      name: subServiceName(master.name, subName),
      subName,
      parentId: master.id,
      // Its own lines only. attachedFields hangs extra documents off an
      // inherited category; steps holds categories of its own.
      data: { attachedFields: {}, steps: [] },
    });
    // Straight into the editor, NOT the creation wizard addService opens by
    // default. The wizard has no concept of a Master, so a new Sub Service
    // rendered through it appears to have no documents at all — and an admin
    // who believes that starts rebuilding categories the Master already
    // provides, ending up with the inherited set and a hand-made duplicate of
    // it once the page next resolves properly.
    //
    // A Sub Service has nothing of its own to be walked through anyway: it
    // opens on its Master's lines, locked, with room to add underneath.
    setIsCreating(false);

    // The new child is selected, so the row holding it has to be open or the
    // editor is sitting on a tab that isn't on screen.
    setMasterOpen((prev) => ({ ...prev, [master.id]: true }));
    setSubServiceDraft(null);
  };

  // Renaming, with the two names a Sub Service has kept in step.
  //
  // A child is renamed by its own half only — the Master's half isn't the
  // admin's to edit from the child — and renaming a Master rewrites every
  // child that carries its name.
  const commitRename = () => {
    if (!renameModal) return;
    const value = renameModal.value.trim();
    if (!value) {
      setRenameModal(null);
      return;
    }
    const updated = services.map((svc: any) => ({ ...svc }));
    const target = updated[renameModal.index];
    if (!target) {
      setRenameModal(null);
      return;
    }

    if (target.parentId) {
      const master = updated.find((svc: any) => svc.id === target.parentId);
      target.subName = value;
      // Without a Master to prepend, the child keeps its own name rather than
      // being given a broken one.
      target.name = master ? subServiceName(master.name, value) : value;
    } else {
      target.name = value;
      if (target.isMaster) {
        for (const svc of updated) {
          if (svc.parentId === target.id && svc.subName) {
            svc.name = subServiceName(value, svc.subName);
          }
        }
      }
    }

    setServices(updated);
    setRenameModal(null);
  };

  // A copy takes the source's structure but NOT its field keys.
  //
  // Everything a user has against a document — the uploaded file, whether it's
  // been issued, approved, notified, and any admin override — is stored under
  // the bare field key with no service beside it (userState.files[key],
  // issued[key], overrides[`${userId}_${key}`]). Copy the keys and the copy
  // silently inherits all of it from the original: open the new service and
  // last month's lease is already sitting there.
  const duplicateService = () => {
    if (!duplicateModal) return;
    const source = services[duplicateModal.sourceIndex];
    if (!source) return;

    const data = JSON.parse(JSON.stringify(source.data ?? { steps: [] }));
    const stamp = Date.now();
    let n = 0;
    for (const step of data.steps || []) {
      for (const field of step.fields || []) {
        n += 1;
        field.key = `doc_${stamp}_${n}`;
      }
    }

    // A copy made along the Master route is a Master. The source's own
    // isMaster is deliberately not carried over — what is being made is
    // decided by the button that opened this modal, not by what was copied.
    addService({
      name: `${source.name} Copy`,
      data,
      ...(duplicateModal.isMaster ? { isMaster: true } : {}),
    });
    setDuplicateModal(null);
  };

  /**
   * A service's name, with a Master's half of it in yellow.
   *
   * A Master reads as yellow, and so does the Master's half of a Sub Service's
   * name — the shared colour is what says they belong to each other. Nothing
   * else in a tab or a checkbox list does.
   *
   * The split is taken from the stored subName rather than by looking for a
   * separator, since a Master's own name may well contain one. If the two have
   * drifted apart the name renders plain rather than sliced in the wrong place.
   *
   * Shared by everywhere real elements are rendered. It can't be used inside a
   * <select>, whose <option>s hold text and not markup.
   */
  const serviceLabel = (svc: any): React.ReactNode => {
    if (svc?.isMaster) {
      return (
        <span className="service-tab-master" title="Master Service">
          {svc.name}
        </span>
      );
    }
    if (
      svc?.subName &&
      typeof svc?.name === "string" &&
      svc.name.endsWith(svc.subName)
    ) {
      return (
        <span title="Sub Service">
          <span className="service-tab-master">
            {svc.name.slice(0, svc.name.length - svc.subName.length)}
          </span>
          {svc.subName}
        </span>
      );
    }
    return <span>{svc?.name}</span>;
  };

  /**
   * Why a service can't be deleted, or null when it can.
   *
   * Read twice: the tab shows a padlock instead of a × when this returns
   * anything, and clicking that padlock shows the same explanation the delete
   * would have shown. One function so the two can't drift — a × that opens a
   * refusal, or a padlock with no reason behind it, are both worse than either.
   */
  const serviceDeleteBlocker = (
    svc: any,
    index: number,
  ): { title: string; message: string } | null => {
    // A Master still holding Sub Services can't go. Its lines are read through
    // every child on every render, so removing it doesn't tidy anything up — it
    // strands them, each one losing the documents it inherits while keeping only
    // what it added.
    const children = svc?.isMaster
      ? services.filter((s: any) => s?.parentId === svc.id)
      : [];
    if (children.length > 0) {
      return {
        title: "Can't Delete Master Service",
        message:
          `"${svc.name}" has ${children.length} Sub Service${children.length === 1 ? "" : "s"} under it` +
          ` — ${children.map((s: any) => s.subName || s.name).join(", ")}.` +
          " Delete them first; a Sub Service reads its documents from the Master and would be left holding only what it added.",
      };
    }

    // Assigned to somebody. Assignments are POSITIONS in this array, so this
    // asks the same question the index shift on delete answers: who is on it?
    const assigned = users.filter((u: any) => {
      const a = assignments[u.user_id];
      if (a?.mode === "single") return a.value === index;
      if (a?.mode === "multi") return (a.values || []).includes(index);
      return false;
    });
    if (assigned.length > 0) {
      const named = assigned
        .slice(0, 5)
        .map((u: any) => u.name || u.email || u.user_id);
      return {
        title: "Can't Delete Service",
        message:
          `"${svc.name}" is assigned to ${assigned.length} user${assigned.length === 1 ? "" : "s"}` +
          ` — ${named.join(", ")}${assigned.length > named.length ? `, and ${assigned.length - named.length} more` : ""}.` +
          " Reassign them first; deleting it now would take away documents they are still being asked for.",
      };
    }

    // Documents already filed against this service's lines.
    //
    // Checked separately from the assignment above, because the two come apart:
    // unassign somebody and their uploads stay exactly where they were. Deleting
    // the service then leaves files in the container and in the user's state that
    // nothing enumerates any more — there is no screen left that asks for those
    // keys.
    //
    // Matched by FIELD KEY, because that is how an upload is stored —
    // `userState.files[key]`, with no service beside it. A duplicated service
    // shares its source's keys, so this can block on a file that arrived through
    // the other one; blocking a delete too often is the safe direction to be
    // wrong in.
    const serviceKeys = new Set<string>();
    for (const step of svc?.data?.steps || []) {
      for (const field of step?.fields || []) {
        if (field?.key) serviceKeys.add(field.key);
      }
    }

    const filedBy = users.filter((u: any) => {
      const state = userStates[u.user_id];
      const files = state?.files || {};
      if (Object.keys(files).length === 0) return false;

      // Lines the admin added on THIS service for THIS user, which are not in
      // the shared schema above.
      const keys = new Set(serviceKeys);
      const slice = getServiceLocalDocs(state?.localDocs, index);
      for (const attached of Object.values(slice.attachedFields || {})) {
        for (const field of (attached as any[]) || []) {
          if (field?.key) keys.add(field.key);
        }
      }
      for (const step of slice.steps || []) {
        for (const field of (step as any)?.fields || []) {
          if (field?.key) keys.add(field.key);
        }
      }

      return [...keys].some((key) => files[key]);
    });

    if (filedBy.length > 0) {
      const named = filedBy
        .slice(0, 5)
        .map((u: any) => u.name || u.email || u.user_id);
      return {
        title: "Can't Delete Service",
        message:
          `"${svc.name}" holds documents filed by ${filedBy.length} user${filedBy.length === 1 ? "" : "s"}` +
          ` — ${named.join(", ")}${filedBy.length > named.length ? `, and ${filedBy.length - named.length} more` : ""}.` +
          " Deleting it would leave those documents with no screen that reads them.",
      };
    }

    return null;
  };

  const serviceTab = (svc: any, index: number) => (
    <div
      key={index}
      className={`template-tab ${
        activeTab === "new" && activeServiceIndex === index ? "active" : ""
      }`}
      onClick={() => {
        setActiveTab("new");
        setActiveServiceIndex(index);
        setIsCreating(false);
        setIsEditing(false);
      }}
      onDoubleClick={() => {
        // A Sub Service is renamed by its own half of the name.
        setRenameModal({
          index,
          value: svc?.parentId ? svc.subName || svc.name : svc.name,
        });
      }}
    >
      {serviceLabel(svc)}

      {/* A service in use loses its × altogether — the strongest guard is not
          offering the action. A padlock takes its place rather than nothing,
          because the admin still needs to find out what is holding it, and
          clicking the padlock is what says so. */}
      {serviceDeleteBlocker(svc, index) ? (
        <span
          className="template-tab-locked"
          title="In use — click to see what is holding it"
          onClick={(e) => {
            e.stopPropagation();
            const blocker = serviceDeleteBlocker(svc, index);
            if (!blocker) return;
            setDeleteConfirm({
              ...blocker,
              blocked: true,
              onConfirm: () => setDeleteConfirm(null),
            });
          }}
        >
          {"🔒"}
        </span>
      ) : (
      <button
        className="template-tab-close"
        onClick={(e) => {
          e.stopPropagation();

          setDeleteConfirm({
            title: "Delete Service",
            message: "Are you sure you want to delete this Service?",
            onConfirm: () => {
              setDeleteConfirm(null);
              const updated = services.filter((_, i) => i !== index);
              setServices(updated);

              // Assignments are POSITIONS in this array, not ids. Removing a
              // service shifts every one after it down, so the assignments
              // have to move with it in the same breath — otherwise everyone
              // assigned past the deleted service silently ends up on their
              // neighbour's. Anyone assigned to the deleted service itself
              // loses the assignment, which is the truth: it's gone.
              const shift = (v: number) => (v > index ? v - 1 : v);
              setAssignments((prev) => {
                const next: typeof prev = {};
                Object.keys(prev).forEach((userId) => {
                  const a = prev[userId];
                  if (a?.mode === "single") {
                    if (a.value === index) return;
                    next[userId] = { mode: "single", value: shift(a.value) };
                  } else if (a?.mode === "multi") {
                    next[userId] = {
                      mode: "multi",
                      values: (a.values || [])
                        .filter((v) => v !== index)
                        .map(shift),
                    };
                  }
                });
                return next;
              });

              if (activeServiceIndex === index) {
                setActiveServiceIndex(null);
                setActiveTab("overview");
              } else if (
                activeServiceIndex !== null &&
                activeServiceIndex > index
              ) {
                // The tab being viewed just moved down one.
                setActiveServiceIndex(activeServiceIndex - 1);
              }
            },
          });
        }}
      >
        ×
      </button>
      )}
    </div>
  );

  return (
    <div className="manage-profiles-page">
      <h1 className="manage-profiles-title">Manage SMRT Docs</h1>

      {servicesError && (
        <div
          role="alert"
          style={{
            maxWidth: "1100px",
            margin: "0 auto 14px",
            background: "#3a1c1c",
            border: "1px solid #5a2a2a",
            color: "#ffb4b4",
            borderRadius: 6,
            padding: "10px 14px",
            fontSize: "0.85rem",
          }}
        >
          {servicesError}
        </div>
      )}

      {/* Traffic light summary */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14, maxWidth: "1100px", margin: "0 auto 14px" }}>
        <div
          title="Needs Admin Action"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficFilter("red"); setIsTrafficModalOpen(true); }}
        >
          <span style={{ fontSize: "1rem" }}>🔴</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{getRedItems(services, assignments, users, userStates, overrides).length}</span>
        </div>
        <div
          title="Awaiting User Action"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficFilter("yellow"); setIsTrafficModalOpen(true); }}
        >
          <span style={{ fontSize: "1rem" }}>🟡</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{getYellowItems(services, assignments, users, userStates, overrides).length}</span>
        </div>
        <div
          title="Unread Items"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficFilter("green"); setIsTrafficModalOpen(true); }}
        >
          <span style={{ fontSize: "1rem" }}>🟢</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{getGreenItems(services, assignments, users, userStates, overrides).length}</span>
        </div>
      </div>

      {/* Two rows: the fixed tabs and the + on top, the services the org has
          added below, revealed by the triangle. */}
      <div className="template-tab-rows">
        <div className="template-tabs">
          <div
            className={`template-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("overview");
            }}
          >
            Overview
          </div>
          {canViewAuditLog && (
            <div
              className={`template-tab ${activeTab === "auditLog" ? "active" : ""}`}
              onClick={() => setActiveTab("auditLog")}
              title="Encrypted-field reveal audit log (Admin / Sensitive)"
            >
              Audit Log
            </div>
          )}
          <div
            className="template-tab template-tab-plus"
            onClick={() => setShowNewServicePanel(true)}
          >
            +
          </div>

          {/* Only offered when there's a row to open. */}
          {services.length > 0 && (
            <button
              className="template-tabs-toggle"
              title={
                serviceTabsOpen
                  ? "Hide services"
                  : `Show services (${topLevelCount})`
              }
              aria-expanded={serviceTabsOpen}
              onClick={() => setServiceTabsOpen((open) => !open)}
            >
              {serviceTabsOpen ? "▾" : "▸"}
              {/* Only while closed — once the row is open you can count them.
                  Sub Services are left out: they are counted by the Master
                  they hang from, so counting them here too would double them. */}
              {!serviceTabsOpen && (
                <span className="template-tabs-toggle-count">
                  {topLevelCount}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Kept mounted so it can animate shut as well as open. */}
        {services.length > 0 && (
          <div
            className={`template-tabs-reveal${serviceTabsOpen ? " open" : ""}`}
            aria-hidden={!serviceTabsOpen}
          >
            <div className="service-tab-rows">
              {/* Everything that isn't a Master or one of its children, on the
                  one row it has always been on. */}
              {standaloneIndices.length > 0 && (
                <div className="template-tabs template-tabs-custom">
                  {standaloneIndices.map((i: number) => serviceTab(services[i], i))}
                </div>
              )}
              {/* Then a Master per row with its Sub Services beneath it. */}
              {masterIndices.map((mi: number) => {
                const children = childIndices(mi);
                return (
                  <div key={services[mi]?.id || mi} className="service-tab-group">
                    <div className="template-tabs template-tabs-custom">
                      {serviceTab(services[mi], mi)}
                      {/* Its own box after the Master, matching the strip's
                          main toggle. Only once there is something to reveal. */}
                      {children.length > 0 && (
                        <button
                          className="template-tabs-toggle"
                          aria-expanded={!!masterOpen[services[mi]?.id]}
                          aria-label={
                            masterOpen[services[mi]?.id]
                              ? `Hide Sub Services of ${services[mi]?.name}`
                              : `Show Sub Services of ${services[mi]?.name}`
                          }
                          onClick={() =>
                            setMasterOpen((prev) => ({
                              ...prev,
                              [services[mi]?.id]: !prev[services[mi]?.id],
                            }))
                          }
                        >
                          {masterOpen[services[mi]?.id] ? "▾" : "▸"}
                          {!masterOpen[services[mi]?.id] && (
                            <span className="template-tabs-toggle-count">
                              {children.length}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                    {children.length > 0 && masterOpen[services[mi]?.id] && (
                      <div className="template-tabs template-tabs-custom service-tab-children">
                        {children.map((i: number) => serviceTab(services[i], i))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div
        className="overview-root"
        style={{ maxWidth: "1100px", margin: "0 auto" }}
      >
        <div className="template-list">
          {activeTab === "auditLog" && canViewAuditLog && (
            <div className="template-card" style={{ padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1rem" }}>
                  Encrypted Field Audit Log
                </h3>
                <span style={{ fontSize: "0.78rem", color: "#9aa0a6" }}>
                  {auditLogLoading
                    ? "Loading…"
                    : `${auditLogEvents.length} event${auditLogEvents.length === 1 ? "" : "s"}`}
                </span>
              </div>

              {auditLogError && (
                <div
                  style={{
                    color: "#ef9a9a",
                    background: "rgba(229, 57, 53, 0.12)",
                    padding: "8px 10px",
                    borderRadius: 4,
                    marginBottom: 12,
                    fontSize: "0.85rem",
                  }}
                >
                  {auditLogError}
                </div>
              )}

              {!auditLogLoading &&
                auditLogEvents.length === 0 &&
                !auditLogError && (
                  <div
                    style={{
                      color: "#9aa0a6",
                      fontSize: "0.85rem",
                      textAlign: "center",
                      padding: "20px 0",
                    }}
                  >
                    No encrypted-field reveals recorded for this organisation
                    yet.
                  </div>
                )}

              {auditLogEvents.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.82rem",
                      color: "#e4e4e4",
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", color: "#9aa0a6" }}>
                        <th style={{ ...auditTh, whiteSpace: "nowrap" }}>When</th>
                        <th style={auditTh}>Document Owner</th>
                        <th style={auditTh}>Document</th>
                        <th style={auditTh}>Field</th>
                        <th style={auditTh}>Revealed By</th>
                        <th style={auditTh}>Reason</th>
                        <th style={{ ...auditTh, whiteSpace: "nowrap" }}>IP</th>
                        <th style={auditTh}>User Agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogEvents.map((e, i) => {
                        const owner = users.find(
                          (u: any) => u.user_id === e.ownerUserId,
                        );
                        const ownerLabel =
                          owner?.name || owner?.email || e.ownerUserId;
                        const revealer = users.find(
                          (u: any) => u.user_id === e.revealedByUserID,
                        );
                        const revealerLabel =
                          revealer?.name ||
                          revealer?.email ||
                          e.revealedByUserID;
                        const when = e.timestamp
                          ? new Date(e.timestamp).toLocaleString()
                          : "—";
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid #222" }}>
                            <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                              {when}
                            </td>
                            <td style={{ padding: "8px 10px" }} title={e.ownerUserId}>
                              {ownerLabel}
                            </td>
                            <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: "0.78rem" }}>
                              {e.issuedKey || "—"}
                            </td>
                            <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: "0.78rem" }}>
                              {e.placementId || "—"}
                            </td>
                            <td style={{ padding: "8px 10px" }} title={e.revealedByUserID}>
                              {revealerLabel}
                            </td>
                            <td style={{ padding: "8px 10px", maxWidth: 260 }}>
                              {e.reason || "—"}
                            </td>
                            <td style={{ padding: "8px 10px", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "0.78rem", color: "#9aa0a6" }}>
                              {e.ipAddress || "—"}
                            </td>
                            <td style={{ padding: "8px 10px", maxWidth: 280, fontSize: "0.75rem", color: "#9aa0a6" }} title={e.userAgent}>
                              {e.userAgent
                                ? e.userAgent.length > 60
                                  ? e.userAgent.slice(0, 60) + "…"
                                  : e.userAgent
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {activeTab === "overview" && (
            <div className="template-card">
              <div
                className="overview-header"
                style={{ gridTemplateColumns: "2fr 3fr 2fr 2fr 1fr" }}
              >
                <div
                  onClick={() => toggleSort("name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  User Name {sortArrow("name")}
                </div>
                <div
                  onClick={() => toggleSort("email")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Email {sortArrow("email")}
                </div>
                <div
                  onClick={() => toggleSort("services")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Assigned Services {sortArrow("services")}
                </div>
                <div
                  onClick={() => toggleSort("lastLogin")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Last Login {sortArrow("lastLogin")}
                </div>
                <div>Actions</div>
              </div>

              {usersLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 16px", color: "#9aa0a6", fontSize: "0.9rem" }}>
                  <span className="spinner" aria-hidden="true" />
                  Loading users…
                </div>
              )}

              {!usersLoading && usersError && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "24px 16px", color: "#e4e4e4", fontSize: "0.9rem" }}>
                  <span style={{ color: "#e57373" }}>{usersError}</span>
                  <button className="template-tab" onClick={() => loadUsers()}>
                    Retry
                  </button>
                </div>
              )}

              {!usersLoading && !usersError && sortedUsers.map((user, i) => (
                <div
                  key={i}
                  className="overview-row"
                  style={{ gridTemplateColumns: "2fr 3fr 2fr 2fr 1fr" }}
                >
                  <div>{user.name || user.email}</div>

                  <div style={{ textAlign: "left" }}>{user.email}</div>

                  <div>
                    <select
                      className="smrtdocs-select"
                      style={
                        assignments[user.user_id]?.mode === "multi"
                          ? { color: "#d4a017" }
                          : {}
                      }
                      value={
                        assignments[user.user_id]?.mode === "multi"
                          ? "multi"
                          : assignments[user.user_id]?.mode === "single"
                            ? (
                                assignments[user.user_id] as {
                                  mode: "single";
                                  value: number;
                                }
                              ).value
                            : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;

                        if (val === "multi") {
                          setAssignments({
                            ...assignments,
                            [user.user_id]: { mode: "multi", values: [] },
                          });
                        } else {
                          setAssignments({
                            ...assignments,
                            [user.user_id]: {
                              mode: "single",
                              value: Number(val),
                            },
                          });
                        }
                      }}
                    >
                      <option value="">- Please Select -</option>
                      {/* Masters are skipped inside the map, never filtered
                          out beforehand: the option's value IS the index into
                          `services`, so removing entries first would renumber
                          every option after the Master and repoint the
                          assignment at the wrong service. */}
                      {services.map((svc, i) =>
                        svc?.isMaster ? null : (
                          <option key={i} value={i}>
                            {svc.name}
                          </option>
                        ),
                      )}
                      <option value="multi" disabled>
                        Multiple
                      </option>
                    </select>

                    {multiOpen[user.user_id] && (
                      <div className="multi-select-panel">
                        {services.map((svc, i) => {
                          // Skipped in place, not filtered out — see the
                          // single-select dropdown above; `i` is the stored
                          // assignment value.
                          if (svc?.isMaster) return null;
                          const selected =
                            assignments[user.user_id]?.mode === "multi"
                              ? (
                                  assignments[user.user_id] as {
                                    mode: "multi";
                                    values: number[];
                                  }
                                ).values
                              : [];

                          return (
                            <label
                              key={i}
                              style={{ display: "block", fontSize: "13px" }}
                            >
                              <input
                                type="checkbox"
                                checked={selected.includes(i)}
                                onChange={(e) => {
                                  let updated = [...selected];

                                  if (e.target.checked) {
                                    updated.push(i);
                                  } else {
                                    updated = updated.filter((x) => x !== i);
                                  }

                                  setAssignments({
                                    ...assignments,
                                    [user.user_id]: {
                                      mode: "multi",
                                      values: updated,
                                    },
                                  });
                                }}
                              />
                              {serviceLabel(svc)}
                            </label>
                          );
                        })}
                        <div style={{ marginTop: "8px" }}>
                          <button
                            className="btn-save"
                            onClick={() => {
                              setMultiOpen({
                                ...multiOpen,
                                [user.user_id]: false,
                              });
                            }}
                          >
                            Save
                          </button>

                          <button
                            className="btn-cancel"
                            onClick={() => {
                              const current = assignments[user.user_id] as any;

                              setMultiOpen({
                                ...multiOpen,
                                [user.user_id]: false,
                              });

                              if (current?._prev) {
                                // restore previous selection
                                setAssignments({
                                  ...assignments,
                                  [user.user_id]: current._prev,
                                });
                              } else {
                                // First-time cancel → clear. The KEY goes,
                                // not just its value: writing undefined left
                                // the user in the map with nothing under it,
                                // and every reader that walks the map with
                                // Object.entries then read .mode off it.
                                const next = { ...assignments };
                                delete next[user.user_id];
                                setAssignments(next);
                              }
                            }}
                            style={{ marginLeft: "8px" }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    {user.last_login
                      ? new Date(user.last_login).toLocaleString()
                      : "—"}
                  </div>

                  <div className="smrtdocs-actions">
                    <button
                      className="btn-icon smrtdocs-btn-view"
                      title="View"
                      onClick={() => {
                        navigate(`/manage-smrt-docs/view/${user.user_id}`, {
                          state: {
                            name: user.name || user.email,
                            email: user.email,
                          },
                        });
                      }}
                    >
                      👁
                    </button>

                    <button
                      className="btn-icon smrtdocs-icon-spacing smrtdocs-btn-multi"
                      title="Assign Multiple Services"
                      onClick={() => {
                        // TODO: consider modal in future
                        const prev = assignments[user.user_id];

                        // Opening the panel carries the CURRENT assignment in,
                        // whichever shape it is. It used to clear a single
                        // assignment to an empty list, so a user with one service
                        // lost it by having their boxes looked at — and the
                        // auto-save wrote that loss out before anything was
                        // ticked.
                        const existing = assignments[user.user_id];
                        const seeded: number[] =
                          existing?.mode === "multi"
                            ? (existing as any).values || []
                            : existing?.mode === "single"
                              ? [(existing as any).value]
                              : [];

                        setAssignments({
                          ...assignments,
                          [user.user_id]: {
                            mode: "multi",
                            values: seeded,
                            _prev: prev,
                          } as any,
                        });

                        setMultiOpen({ ...multiOpen, [user.user_id]: true });
                      }}
                    >
                      ☑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "new" && (
            <div className="template-card">
              {isCreating ? (
                <ManageSMRTDocsWizard
                  service={services[activeServiceIndex!]}
                  setService={(updated: any) => {
                    const copy = [...services];
                    copy[activeServiceIndex!] = updated;
                    setServices(copy);
                  }}
                  onClose={() => {
                    setActiveTab("overview");
                  }}
                />
              ) : activeServiceIndex !== null &&
                isSubService(services[activeServiceIndex]) ? (
                /* A Sub Service is edited the same way an admin edits one
                   person's copy of a service: the inherited lines locked, its
                   own added underneath. The editor already does exactly that,
                   so this hands it the Master's steps as the locked set rather
                   than growing a second mode that would drift from the first.

                   Nothing is copied in — the child stores only what it adds,
                   and the Master's lines are read through it on every render,
                   which is what makes an edit to the Master arrive here. */
                <SMRTDocsEditor
                  serviceName={services[activeServiceIndex].name}
                  setServiceName={(name: string) => {
                    const copy = [...services];
                    copy[activeServiceIndex].name = name;
                    setServices(copy);
                  }}
                  template={{ steps: [] }}
                  setTemplate={() => {}}
                  lockedSteps={
                    findMaster(services, services[activeServiceIndex])?.data
                      ?.steps || []
                  }
                  /* Passed so the editor knows it is on a page rather than in
                     the per-user modal, and puts up its own Edit / Save /
                     Cancel. Without them a Sub Service was permanently live:
                     every keystroke went straight to the auto-save, with no
                     Cancel to back out of a change. */
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  localTemplate={ownContent(services[activeServiceIndex])}
                  setLocalTemplate={(updated: any) => {
                    const copy = [...services];
                    copy[activeServiceIndex] = {
                      ...copy[activeServiceIndex],
                      data: {
                        attachedFields: updated?.attachedFields || {},
                        steps: updated?.steps || [],
                        // This service's own version of the Master's lines —
                        // renamed, retyped, made mandatory, hidden. Listed
                        // explicitly, like the two above: this writes the whole
                        // `data`, so a key left out here is a key erased on the
                        // next keystroke.
                        fieldOverrides: updated?.fieldOverrides || {},
                      },
                    };
                    setServices(copy);
                  }}
                />
              ) : activeServiceIndex !== null ? (
                <SMRTDocsEditor
                  serviceName={services[activeServiceIndex].name}
                  setServiceName={(name: string) => {
                    const copy = [...services];
                    copy[activeServiceIndex].name = name;
                    setServices(copy);
                  }}
                  template={services[activeServiceIndex].data}
                  setTemplate={(updated: any) => {
                    const copy = [...services];
                    copy[activeServiceIndex].data = updated;
                    setServices(copy);
                  }}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
      <SMRTDocsTrafficModal
        isOpen={isTrafficModalOpen}
        onClose={() => setIsTrafficModalOpen(false)}
        filter={trafficFilter}
        services={services}
        assignments={assignments}
        users={users}
        userStates={userStates}
        overrides={overrides}
      />
      <DeleteConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={deleteConfirm?.onConfirm || (() => {})}
        title={deleteConfirm?.title || ""}
        message={deleteConfirm?.message || ""}
        confirmLabel={deleteConfirm?.blocked ? "OK" : "Delete"}
        confirmColor={deleteConfirm?.blocked ? "#4b5563" : undefined}
      />
      {showNewServicePanel && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Create New Service</h2>
            </div>
            <div className="smrtdocs-modal-body">
              {/* Each hands off to its own modal and closes this one, so only
                  one dialog is ever up; Cancel there brings this one back. */}
              <div className="new-template-actions is-stacked">
                <button
                  className="btn-add"
                  onClick={() => {
                    setNewServiceDraft({ name: "", isMaster: false });
                    setShowNewServicePanel(false);
                  }}
                >
                  New Service
                </button>
                <button
                  className="btn-add"
                  disabled={serviceCopySources.length === 0}
                  title={
                    serviceCopySources.length === 0
                      ? "There's no service to copy yet"
                      : undefined
                  }
                  onClick={() => {
                    setDuplicateModal({
                      sourceIndex: defaultCopySource(serviceCopySources),
                      isMaster: false,
                    });
                    setShowNewServicePanel(false);
                  }}
                >
                  Duplicate Existing
                </button>
                {/* A Master is the template Sub Services inherit from. Built
                    the same two ways as any other service; it just can't be
                    assigned to anyone. Sub Service itself is still unwired —
                    it needs services to address each other by their permanent
                    svc_ id rather than by array position. */}
                <button
                  className="btn-add"
                  onClick={() => {
                    setShowMasterPanel(true);
                    setShowNewServicePanel(false);
                  }}
                >
                  Master
                </button>
                <button
                  className="btn-add"
                  onClick={() => {
                    if (!hasMaster) {
                      setNoMasterWarning(true);
                      setShowNewServicePanel(false);
                      return;
                    }
                    // Opens on the first Master; with only one there is
                    // nothing to choose and the picker stays hidden.
                    setSubServiceDraft({ name: "", parentIndex: masterIndices[0] });
                    setShowNewServicePanel(false);
                  }}
                >
                  Sub Service
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "24px" }}>
                <button
                  className="btn-cancel"
                  onClick={() => setShowNewServicePanel(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showMasterPanel && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Create Master</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <div className="new-template-actions is-stacked">
                <button
                  className="btn-add"
                  onClick={() => {
                    setNewServiceDraft({ name: "", isMaster: true });
                    setShowMasterPanel(false);
                  }}
                >
                  New Master
                </button>
                <button
                  className="btn-add"
                  disabled={masterCopySources.length === 0}
                  title={
                    masterCopySources.length === 0
                      ? "There's no service to copy yet"
                      : undefined
                  }
                  onClick={() => {
                    setDuplicateModal({
                      sourceIndex: defaultCopySource(masterCopySources),
                      isMaster: true,
                    });
                    setShowMasterPanel(false);
                  }}
                >
                  Duplicate Existing
                </button>
              </div>
              {/* Back to the chooser, matching how Cancel behaves in the other
                  modals this one sits beside. */}
              <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "24px" }}>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowMasterPanel(false);
                    setShowNewServicePanel(true);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {subServiceDraft !== null && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>New Sub Service</h2>
            </div>
            <div className="smrtdocs-modal-body">
              {/* Only asked when there is a choice to make. */}
              {masterIndices.length > 1 && (
                <>
                  <label
                    htmlFor="sub-service-master"
                    style={{ display: "block", marginBottom: 6 }}
                  >
                    Which Master does it belong to?
                  </label>
                  <select
                    id="sub-service-master"
                    className="manage-input"
                    value={subServiceDraft.parentIndex}
                    onChange={(e) =>
                      setSubServiceDraft({
                        ...subServiceDraft,
                        parentIndex: Number(e.target.value),
                      })
                    }
                    style={{ width: "100%", marginBottom: "20px" }}
                  >
                    {masterIndices.map((i: number) => (
                      <option key={i} value={i}>
                        {services[i]?.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <label htmlFor="sub-service-name" style={{ display: "block", marginBottom: 6 }}>
                Sub Service Name
              </label>
              <input
                id="sub-service-name"
                className="manage-input"
                value={subServiceDraft.name}
                autoFocus
                onChange={(e) =>
                  setSubServiceDraft({ ...subServiceDraft, name: e.target.value })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") createSubService();
                }}
                style={{ width: "100%", marginBottom: "12px" }}
              />
              {/* The full name it will be filed under, coloured as it will
                  appear in the tab row. */}
              <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 20px" }}>
                <span className="service-tab-master">
                  {services[subServiceDraft.parentIndex]?.name} -{" "}
                </span>
                <span style={{ color: subServiceDraft.name.trim() ? undefined : "#6f6f6f" }}>
                  {subServiceDraft.name.trim() || "Sub Service Name"}
                </span>
              </p>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setSubServiceDraft(null);
                    setShowNewServicePanel(true);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  disabled={
                    !subServiceDraft.name.trim() ||
                    !services[subServiceDraft.parentIndex]?.id
                  }
                  onClick={createSubService}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {noMasterWarning && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>No Master Service</h2>
            </div>
            <div className="smrtdocs-modal-body">
              {/* Same type and spacing as DeleteConfirmModal's body, so the
                  warning reads as one of the family rather than its own thing. */}
              <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 16px" }}>
                Please create a Master first.
              </p>
              {/* Back to the chooser rather than closed outright — the next
                  thing to do is right there on it. */}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  className="btn-save"
                  onClick={() => {
                    setNoMasterWarning(false);
                    setShowNewServicePanel(true);
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {newServiceDraft !== null && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                {newServiceDraft.isMaster ? "New Master" : "New Service"}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <label htmlFor="new-service-name" style={{ display: "block", marginBottom: 6 }}>
                {newServiceDraft.isMaster ? "Master Name" : "Service Name"}
              </label>
              <input
                id="new-service-name"
                className="manage-input"
                value={newServiceDraft.name}
                autoFocus
                onChange={(e) =>
                  setNewServiceDraft({ ...newServiceDraft, name: e.target.value })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") createBlankService();
                }}
                style={{ width: "100%", marginBottom: newServiceDraft.isMaster ? "12px" : "20px" }}
              />
              {newServiceDraft.isMaster && (
                <p style={{ fontSize: "0.8rem", lineHeight: 1.5, color: "#9a9a9a", margin: "0 0 20px" }}>
                  A Master is a template for Sub Services. It can't be assigned
                  to users.
                </p>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    // Back to whichever chooser opened this one.
                    const master = newServiceDraft.isMaster;
                    setNewServiceDraft(null);
                    if (master) setShowMasterPanel(true);
                    else setShowNewServicePanel(true);
                  }}
                >
                  Cancel
                </button>
                {/* Grey until there's a name to create it under. */}
                <button
                  className="btn-save"
                  disabled={!newServiceDraft.name.trim()}
                  onClick={createBlankService}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {duplicateModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                {duplicateModal.isMaster ? "Duplicate as Master" : "Duplicate Service"}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <label
                htmlFor="duplicate-service-source"
                style={{ display: "block", marginBottom: 6 }}
              >
                Which service do you want to duplicate?
              </label>
              <select
                id="duplicate-service-source"
                className="manage-input"
                value={duplicateModal.sourceIndex}
                autoFocus
                onChange={(e) =>
                  setDuplicateModal({
                    ...duplicateModal,
                    sourceIndex: Number(e.target.value),
                  })
                }
                style={{ width: "100%", marginBottom: duplicateModal.isMaster ? "12px" : "20px" }}
              >
                {(duplicateModal.isMaster
                  ? masterCopySources
                  : serviceCopySources
                ).map((i: number) => (
                  <option key={i} value={i}>
                    {services[i]?.name}
                  </option>
                ))}
              </select>
              {duplicateModal.isMaster && (
                <p style={{ fontSize: "0.8rem", lineHeight: 1.5, color: "#9a9a9a", margin: "0 0 20px" }}>
                  The copy becomes a Master: a template for Sub Services, and
                  not assignable to users.
                </p>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    // Back to whichever chooser opened this one.
                    const master = duplicateModal.isMaster;
                    setDuplicateModal(null);
                    if (master) setShowMasterPanel(true);
                    else setShowNewServicePanel(true);
                  }}
                >
                  Cancel
                </button>
                <button className="btn-save" onClick={duplicateService}>
                  Duplicate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {renameModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                {services[renameModal.index]?.parentId
                  ? "Rename Sub Service"
                  : services[renameModal.index]?.isMaster
                    ? "Rename Master"
                    : "Rename Service"}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <input
                className="manage-input"
                value={renameModal.value}
                autoFocus
                onChange={(e) => setRenameModal({ ...renameModal, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                }}
                style={{ width: "100%", marginBottom: "12px" }}
              />
              {/* A child is renamed by its own half, so show the whole name it
                  will end up with; a Master's rename reaches its children. */}
              {services[renameModal.index]?.parentId ? (
                <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 20px" }}>
                  <span className="service-tab-master">
                    {
                      services.find(
                        (svc: any) =>
                          svc.id === services[renameModal.index]?.parentId,
                      )?.name
                    }{" "}
                    -{" "}
                  </span>
                  <span style={{ color: renameModal.value.trim() ? undefined : "#6f6f6f" }}>
                    {renameModal.value.trim() || "Sub Service Name"}
                  </span>
                </p>
              ) : services[renameModal.index]?.isMaster &&
                childIndices(renameModal.index).length > 0 ? (
                <p style={{ fontSize: "0.8rem", lineHeight: 1.5, color: "#9a9a9a", margin: "0 0 20px" }}>
                  Renames {childIndices(renameModal.index).length} Sub Service
                  {childIndices(renameModal.index).length === 1 ? "" : "s"} with it.
                </p>
              ) : (
                <div style={{ marginBottom: "8px" }} />
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="btn-cancel" onClick={() => setRenameModal(null)}>Cancel</button>
                <button className="btn-save" onClick={commitRename}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
